/** QuickHouse — Éditeur 2D : canvas, événements souris/clavier, pan/zoom, boucle de rendu */
import { useEffect, useRef, useCallback } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import { draw } from './render2d.js';
import { toolHandlers } from './tools2d.js';

export default function CanvasEditor() {
  const canvasRef = useRef(null);
  // Caméra 2D : reprise du store si on y a déjà navigué (init paresseuse, UNE fois),
  // sinon vue par défaut (0.6 px/cm). Persistée au démontage → un aller-retour vers
  // la 3D ne remet plus le plan à sa position de départ.
  const viewRef = useRef(null);
  if (viewRef.current === null) {
    const saved = useProjectStore.getState().view2d;
    viewRef.current = saved ? { ...saved } : { panX: 120, panY: 120, zoom: 0.6 };
  }
  const uiRef = useRef({ preview: null, snapPoint: null, hover: null });
  const tsRef = useRef({});      // état de l'outil en cours
  const panRef = useRef(null);   // drag de panoramique
  const rafRef = useRef(0);

  const requestRender = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const state = useProjectStore.getState();
      const floor = state.activeFloor();
      const project = state.project;
      const lowerFloor = floor && project
        ? project.floors
          .filter((f) => f.level < floor.level)
          .sort((p, q) => q.level - p.level)[0] ?? null
        : null;
      const dpr = window.devicePixelRatio || 1;
      draw(ctx, {
        width: canvas.width / dpr,
        height: canvas.height / dpr,
        dpr,
        floor,
        lowerFloor,
        view: viewRef.current,
        ui: { ...uiRef.current, selection: state.selection },
        catalog: state.catalog,
        gridSize: project?.settings.grid.size ?? 10,
        north: project?.settings.north ?? 0,
      });
    });
  }, []);

  const setUi = useCallback((patch) => {
    Object.assign(uiRef.current, patch);
    if ('status' in patch) useProjectStore.getState().setStatus(patch.status ?? '');
    requestRender();
  }, [requestRender]);

  // ── Contexte passé aux outils ──
  const makeCtx = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const view = viewRef.current;
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const state = useProjectStore.getState();
    return {
      store: useProjectStore,
      floor: state.activeFloor(),
      settings: state.project?.settings,
      view, // caméra 2D (pan/zoom) — l'outil « origine » la décale pour éviter un saut
      world: { x: (sx - view.panX) / view.zoom, y: (sy - view.panY) / view.zoom },
      screen: { x: sx, y: sy },
      tol: 12 / view.zoom, // tolérance : 12 px écran, en cm monde
      shift: e.shiftKey,
      ctrl: e.ctrlKey || e.metaKey,
      ts: tsRef.current,
      setUi,
    };
  }, [setUi]);

  // ── Événements ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const currentTool = () => toolHandlers[useProjectStore.getState().tool];
    const editable = () => !useProjectStore.getState().chatBusy && useProjectStore.getState().activeFloor();

    const onPointerDown = (e) => {
      canvas.setPointerCapture(e.pointerId);
      if (e.button === 1 || (e.button === 0 && e.altKey)) { // molette ou Alt+clic : panoramique
        panRef.current = { x: e.clientX, y: e.clientY };
        e.preventDefault();
        return;
      }
      if (e.button === 2) {
        if (editable()) currentTool().onCancel?.(makeCtx(e));
        return;
      }
      if (e.button === 0 && editable()) currentTool().onDown?.(makeCtx(e));
      requestRender();
    };

    const onPointerMove = (e) => {
      if (panRef.current) {
        const view = viewRef.current;
        view.panX += e.clientX - panRef.current.x;
        view.panY += e.clientY - panRef.current.y;
        panRef.current = { x: e.clientX, y: e.clientY };
        requestRender();
        return;
      }
      if (editable()) currentTool().onMove?.(makeCtx(e));
      requestRender();
    };

    const onPointerUp = (e) => {
      if (panRef.current) { panRef.current = null; return; }
      if (editable()) currentTool().onUp?.(makeCtx(e));
      requestRender();
    };

    const onWheel = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const view = viewRef.current;
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const factor = Math.exp(-e.deltaY * 0.0012);
      // 80 px/cm maxi : un carreau de 1 mm fait alors 8 px, confortable pour la précision fine
      const newZoom = Math.max(0.03, Math.min(80, view.zoom * factor));
      view.panX = sx - ((sx - view.panX) / view.zoom) * newZoom;
      view.panY = sy - ((sy - view.panY) / view.zoom) * newZoom;
      view.zoom = newZoom;
      requestRender();
    };

    const onKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const state = useProjectStore.getState();
      if (e.ctrlKey && e.key.toLowerCase() === 'z') { e.preventDefault(); state.undo(); requestRender(); return; }
      if (e.ctrlKey && e.key.toLowerCase() === 'y') { e.preventDefault(); state.redo(); requestRender(); return; }
      if (e.ctrlKey && e.key.toLowerCase() === 's') { e.preventDefault(); state.saveNow(); return; }
      // Raccourcis d'outils
      const map = { s: 'select', w: 'wall', p: 'door', f: 'window', g: 'garage', o: 'passage', e: 'stair', t: 'room', d: 'slab', c: 'roof', b: 'item', u: 'beam', m: 'measure', x: 'delete' };
      if (!e.ctrlKey && map[e.key.toLowerCase()]) {
        tsRef.current = {};
        setUi({ preview: null, snapPoint: null, status: '' });
        state.setTool(map[e.key.toLowerCase()]);
        return;
      }
      if (e.key === 'Escape') {
        toolHandlers[state.tool].onKey?.({ ...fakeCtx(), ts: tsRef.current, setUi, store: useProjectStore }, 'Escape');
        state.setSelection(null);
        requestRender();
        return;
      }
      if (editable()) toolHandlers[state.tool].onKey?.({ ...fakeCtx(), ts: tsRef.current, setUi, store: useProjectStore }, e.key);
      requestRender();
    };

    // Contexte minimal pour les événements clavier (pas de position souris)
    const fakeCtx = () => {
      const state = useProjectStore.getState();
      return { floor: state.activeFloor(), settings: state.project?.settings, world: { x: 0, y: 0 }, tol: 12, shift: false };
    };

    const onContextMenu = (e) => e.preventDefault();

    const onDblClick = (e) => {
      if (editable()) currentTool().onDbl?.(makeCtx(e));
      requestRender();
    };
    canvas.addEventListener('dblclick', onDblClick);

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('contextmenu', onContextMenu);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      canvas.removeEventListener('dblclick', onDblClick);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [makeCtx, requestRender, setUi]);

  // ── Redimensionnement + abonnement au store ──
  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = canvas.parentElement;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = parent.clientWidth * dpr;
      canvas.height = parent.clientHeight * dpr;
      canvas.style.width = `${parent.clientWidth}px`;
      canvas.style.height = `${parent.clientHeight}px`;
      requestRender();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(parent);
    const unsub = useProjectStore.subscribe((s) => [s.project, s.selection, s.activeFloorId, s.tool], requestRender);
    return () => { ro.disconnect(); unsub(); };
  }, [requestRender]);

  // Persiste la caméra 2D au démontage (bascule vers la 3D, retour aux projets)
  useEffect(() => () => useProjectStore.getState().setView2d({ ...viewRef.current }), []);

  // Curseur selon l'outil
  const tool = useProjectStore((s) => s.tool);
  const roomMode = useProjectStore((s) => s.roomMode);
  useEffect(() => {
    if (canvasRef.current) canvasRef.current.style.cursor = toolHandlers[tool]?.cursor ?? 'default';
    tsRef.current = {};
    setUi({ preview: null, snapPoint: null, guides: null });
  }, [tool, roomMode, setUi]);

  return <canvas ref={canvasRef} />;
}
